import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import { useScrollLock } from 'V2/Hooks/useScrollLock';

import styles from './styles.module.css';
import close from '../Assets/close.svg';
import { Title } from './Content';
import cx from 'classnames';

export type ModalConfigType = {
  showModal: boolean;
  title?: string;
  width?: number | string;
  badge?: string;
  closeOnOverlayClick?: boolean;
  closeAction?: () => void;
  wrapperClassName?: string;
  containerClassName?: string;
  children: React.ReactNode;
};

export const Modal: React.FC<ModalConfigType> = ({
  showModal,
  children,
  title,
  width = '455px',
  badge,
  closeOnOverlayClick = true,
  closeAction,
  wrapperClassName,
  containerClassName,
}) => {
  useScrollLock(document.body, showModal);

  const renderBadge = () => {
    return (
      <div className={styles.badge}>
        <span>{badge}</span>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {showModal && (
        <motion.div
          key="modal-animation"
          initial={'out'}
          animate={'in'}
          exit={'out'}
          variants={{
            in: {
              transition: {
                staggerDirection: 1,
                staggerChildren: 0.1,
              },
            },
            out: {
              transition: {
                staggerDirection: -1,
                staggerChildren: 0.1,
              },
            },
          }}
          className={styles.main}
        >
          <motion.div
            variants={{
              in: {
                opacity: 1,
                ...(closeOnOverlayClick
                  ? {}
                  : { userSelect: 'none', pointerEvents: 'none' }),
              },
              out: { opacity: 0 },
            }}
            transition={{ duration: 0.25 }}
            className={styles.overlay}
            {...(closeOnOverlayClick ? { onClick: closeAction } : {})}
          />
          <motion.div
            className={cx(styles.wrapper, wrapperClassName)}
            style={{ width }}
          >
            <motion.div
              variants={{
                in: { y: 0, opacity: 1 },
                out: { y: 40, opacity: 0 },
              }}
              transition={{ duration: 0.25 }}
              className={cx(styles.container, containerClassName)}
              data-testid="modal"
            >
              {title && (
                <div className={styles.titleRowContainer}>
                  {!!badge && renderBadge()}
                  <Title>{title}</Title>
                  {closeAction && (
                    <button
                      type={'button'}
                      className={styles.close}
                      onClick={closeAction}
                    >
                      <img height={14} width={14} src={close} alt="close" />
                    </button>
                  )}
                </div>
              )}
              {children}
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
